/**
 * 收藏/关注服务：登录态走 API（跨设备同步）；未登录降级 localStorage
 */
import { api } from './api'

export interface FavoriteItem {
  id: string            // productId
  name: string
  price: number         // 元
  image: string
}

export interface FollowItem {
  brandId: string
  name: string
}

const FAV_KEY = 'phygital_favorites'
const FOLLOW_KEY = 'phygital_follows'

const localGet = <T>(key: string): T[] => {
  try { return JSON.parse(localStorage.getItem(key) || '[]') } catch { return [] }
}
const localSet = (key: string, val: unknown[]) => localStorage.setItem(key, JSON.stringify(val))

export const engagementService = {
  // ─── 收藏 ───
  async listFavorites(userId?: string | null): Promise<FavoriteItem[]> {
    if (userId) {
      const rows = await api.get<any[]>('/users/favorites')
      return (rows ?? []).map((r) => ({
        id: r.productId, name: r.productName, price: (r.price ?? 0) / 100, image: r.productImage ?? '',
      }))
    }
    return localGet<FavoriteItem>(FAV_KEY)
  },

  async addFavorite(product: { id: string; name: string; price: number; image: string }, userId?: string | null) {
    if (userId) {
      await api.post(`/users/favorites/${product.id}`)
    } else {
      const favs = localGet<FavoriteItem>(FAV_KEY)
      if (!favs.some((f) => f.id === product.id)) localSet(FAV_KEY, [...favs, product])
    }
  },

  async removeFavorite(productId: string, userId?: string | null) {
    if (userId) {
      await api.delete(`/users/favorites/${productId}`)
    } else {
      localSet(FAV_KEY, localGet<FavoriteItem>(FAV_KEY).filter((f) => f.id !== productId))
    }
  },

  /** 登录后一次性迁移本地收藏到服务端 */
  async migrateLocalFavorites(userId: string) {
    void userId // 语义标记：迁移目标用户
    const local = localGet<FavoriteItem>(FAV_KEY)
    for (const item of local) {
      await api.post(`/users/favorites/${item.id}`).catch(() => null)
    }
    if (local.length) localStorage.removeItem(FAV_KEY)
    return local.length
  },

  // ─── 关注 ───
  async listFollows(userId?: string | null): Promise<FollowItem[]> {
    if (userId) {
      const rows = await api.get<any[]>('/users/follows')
      return (rows ?? []).map((r) => ({ brandId: r.brandId, name: r.brandName }))
    }
    return localGet<FollowItem>(FOLLOW_KEY)
  },

  async follow(brand: FollowItem, userId?: string | null) {
    if (userId) {
      await api.post(`/users/follows/${brand.brandId}`)
    } else {
      const follows = localGet<FollowItem>(FOLLOW_KEY)
      if (!follows.some((f) => f.brandId === brand.brandId)) localSet(FOLLOW_KEY, [...follows, brand])
    }
  },

  async unfollow(brandId: string, userId?: string | null) {
    if (userId) {
      await api.delete(`/users/follows/${brandId}`)
    } else {
      localSet(FOLLOW_KEY, localGet<FollowItem>(FOLLOW_KEY).filter((f) => f.brandId !== brandId))
    }
  },
}

export default engagementService
